import React from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { EMPTY_VALUE } from '@src/core/config';
import { ArcStack, ArcText } from '@src/core/ui';
import { OrderIdParams } from '@src/core/entities';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetUserDataQuery } from '@src/core/features';
import { resetState, selectDestinationLocation, selectTakeOffLocation, setDestinationLocation, setTakeOffLocation, useCreateOrderMutation, useUpdateOrderMutation } from './redux';
import { CreateSidebarProps, UpdateSidebarProps, UpsertOrderSidebarProps } from './types';
import { OrderForm, SidebarLoading } from './components';
import { useCancelOrderMutation, useGetOrderQuery } from '@src/features/order/redux';
import { IOrderForm } from './entities';
import { routesConfig } from '@src/core/routes';
import { selectOrderFormData } from '../../pages/orders-page/redux';

const toPath = '/app/orders/create-mission';

const UpsertOrderSidebar: React.FC<UpsertOrderSidebarProps> = () => {

  const dispatch = useDispatch();
  const [queryParams] = useSearchParams();
  const params = useParams<OrderIdParams>();
  
  const isStepByStep = Boolean(queryParams.get('step-by-step'));

  const { data: userData } = useGetUserDataQuery();

  const takeOffLocation = useSelector(selectTakeOffLocation);
  const destinationLocation = useSelector(selectDestinationLocation);
  const orderFormData = useSelector(selectOrderFormData);
  
  const form = useForm<IOrderForm>({
    defaultValues: {
      clientName: EMPTY_VALUE,
      packageContent: EMPTY_VALUE,
      destinationLocation: {
        address: EMPTY_VALUE,
        latitude: EMPTY_VALUE,
        longitude: EMPTY_VALUE,
      },
      takeOffLocation: {
        address: userData?.hub?.name ?? EMPTY_VALUE,
        latitude: userData?.hub?.latitude ?? EMPTY_VALUE,
        longitude: userData?.hub?.longitude ?? EMPTY_VALUE,
      }
    }
  });

  // Prefill form with orderFormData if available (from Repeat button)
  React.useEffect(() => {
    if (orderFormData && !params.orderId) {
      const orderData = orderFormData as any;
      
      form.setValue('clientName', orderData.clientName || EMPTY_VALUE);
      form.setValue('packageContent', orderData.packageContent || EMPTY_VALUE);
      
      if (orderData.deliveryLocation) {
        const destLocation = {
          address: orderData.deliveryLocation.address || EMPTY_VALUE,
          latitude: String(orderData.deliveryLocation.latitude || EMPTY_VALUE),
          longitude: String(orderData.deliveryLocation.longitude || EMPTY_VALUE),
        };
        form.setValue('destinationLocation', destLocation);
        dispatch(setDestinationLocation(destLocation));
      }
      
      if (orderData.takeoffLocation) {
        const takeOffLoc = {
          address: orderData.takeoffLocation.address || EMPTY_VALUE,
          latitude: String(orderData.takeoffLocation.latitude || EMPTY_VALUE),
          longitude: String(orderData.takeoffLocation.longitude || EMPTY_VALUE),
        };
        form.setValue('takeOffLocation', takeOffLoc);
        dispatch(setTakeOffLocation(takeOffLoc));
      } else if (userData?.hub) {
        const hubLocation = {
          address: userData.hub.name || EMPTY_VALUE,
          latitude: String(userData.hub.latitude || EMPTY_VALUE),
          longitude: String(userData.hub.longitude || EMPTY_VALUE),
        };
        form.setValue('takeOffLocation', hubLocation);
        dispatch(setTakeOffLocation(hubLocation));
      }
    }
  }, [orderFormData, params.orderId, form, dispatch, userData]);

  React.useEffect(() => {
    if (!takeOffLocation) return;
    form.setValue('takeOffLocation', takeOffLocation);
  }, [takeOffLocation, form.setValue]);

  React.useEffect(() => {
    if (!destinationLocation) return;
    form.setValue('destinationLocation', destinationLocation);
  }, [destinationLocation, form.setValue]);

  React.useEffect(() => {
    return () => { dispatch(resetState()); };
  }, []);

  if (params.orderId) {
    return (
      <UpdateSidebar form={form} isStepByStep={isStepByStep} />
    );
  }

  return (
    <CreateSidebar form={form} />
  );
};



const CreateSidebar: React.FC<CreateSidebarProps> = (props) => {
  const { form } = props;

  const navigate = useNavigate();
  const [createOrder, { isLoading: isSubmitting }] = useCreateOrderMutation();

  const onCreateOrder = (data: IOrderForm) => {
    createOrder(data).unwrap().then((response) => {
      toast.success('Order has been created');
      navigate(`${toPath}/${response.id}?step-by-step=true`, { replace: true });
    }).catch(() => {});
  };

  const goBack = () => navigate(-1);

  return (
    <ArcStack spacing={3} height='100%'>
      <ArcText.h4> 1. Enter delivery details </ArcText.h4>
      <OrderForm 
        form={form} 
        isSubmitting={isSubmitting} 

        goBack={goBack}
        onSubmit={onCreateOrder} />
    </ArcStack>
  );
};

const UpdateSidebar: React.FC<UpdateSidebarProps> = (props) => {
  const { form, isStepByStep } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams<OrderIdParams>();

  const id = Number(params.orderId);

  const [cancelOrder, { isLoading: isCanceling }] = useCancelOrderMutation();
  const [updateOrder, { isLoading: isSubmitting }] = useUpdateOrderMutation();
  

  const { data: order, isLoading } = useGetOrderQuery(id);

  React.useEffect(() => {
    if (!order) return;
    form.setValue('clientName', order.clientName);
    form.setValue('packageContent', order.packageContent);
    form.setValue('takeOffLocation', order.takeoffLocation);
    form.setValue('destinationLocation', order.destinationLocation);
    dispatch(setTakeOffLocation(order.takeoffLocation));
    dispatch(setDestinationLocation(order.destinationLocation));
  }, [order]);

  const onUpdateOrder = (data: IOrderForm) => {
    updateOrder({ id, ...data }).unwrap().then((response) => {
      toast.success('Order has been updated');
      if (isStepByStep) {
        navigate(`${toPath}/${response.id}?step-by-step=true`, { replace: true });
        return;
      }
      navigate(-1);
    }).catch(() => {
      // Error toast handled by rtkErrorLogger middleware
    });
  };

  const onCancel = () => {
    cancelOrder(id).unwrap().then(() => {
      toast.success('Order has been canceled');
      if (isStepByStep) {
        navigate(routesConfig.orders, { replace: true  });
        return;
      }
      navigate(-1);
    }).catch(() => {
      // Error toast handled by rtkErrorLogger middleware
    });
  };
  
  if (isLoading) return <SidebarLoading />;

  return (
    <ArcStack spacing={3} height='100%'>
      <ArcText.h4> 1. Enter delivery details </ArcText.h4>
      <OrderForm 
        form={form} 
        isCanceling={isCanceling}
        isSubmitting={isSubmitting} 
        goBack={onCancel}
        onSubmit={onUpdateOrder} />
    </ArcStack>
  );
};

export default UpsertOrderSidebar;